import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  Checkbox,
  Switch,
  FormControlLabel,
  IconButton,
  Stack,
  Button,
} from "@mui/material";

// Types
export type PermissionKey = "read" | "create" | "update" | "delete";

export interface PermissionState {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface ModulePermission {
  module: string;
  permissions: PermissionState;
}

interface PermissionCardProps {
  moduleName: string;
  isViewMode:boolean;
  icon?: React.ReactNode;
  onChange?: (data: ModulePermission) => void;
  roleDetails?:any,
}

const defaultPermissions: PermissionState = {
  read: false,
  create: false,
  update: false,
  delete: false,
};

const PermissionCard: React.FC<PermissionCardProps> = ({
  moduleName,
  icon,
  onChange,
  roleDetails,
  isViewMode
}) => {
  const [permissions, setPermissions] = useState<PermissionState>(
    defaultPermissions
  );

  let availableActions = ["create", "read", "update", "delete"]
  if (['services','rewards','jobs','needs'].includes(moduleName)) {
    availableActions.shift()
  }


  const handleToggle = (key: PermissionKey, value: boolean) => {
    console.log(key, value, "key, value");
    let updated = { ...permissions, [key]: value };
    if ((key == 'create' || key == 'update' || key == 'delete') && value) {
      updated = { ...updated, read: true };
    }
    setPermissions(updated);
    onChange?.({ module: moduleName, permissions: updated });
  };

  const handleSelectAll = () => {
    const updated: PermissionState = {
      read: true,
      create: true,
      update: true,
      delete: true,
    };
    setPermissions(updated);
    onChange?.({ module: moduleName, permissions: updated });
  };

  const handleClearAll = () => {
    setPermissions({read:false,create:false,update:false,delete:false});
    onChange?.({ module: moduleName, permissions: {read:false,create:false,update:false,delete:false} });
  };

  useEffect(() => {
  if (roleDetails?.actions) {
    const selectedPermissions: PermissionState = {
      read: false,
      create: false,
      update: false,
      delete: false,
    };

    roleDetails.actions.forEach((m: PermissionKey) => {
      selectedPermissions[m] = true;
    });

    setPermissions(selectedPermissions);
  } else {
    setPermissions({
      read: false,
      create: false,
      update: false,
      delete: false,
    });
  }
}, [roleDetails]);

  const renderRow = (label: PermissionKey) => (
    <Box
      key={label}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      py={1}
    >
      <Typography textTransform="capitalize">{label}</Typography>
      <Stack direction="row" spacing={2} alignItems="center">
        <Switch
          checked={permissions[label]}
          disabled={(permissions['create'] || permissions['delete'] || permissions['update']) && label=='read' || isViewMode}
          onChange={(e) => handleToggle(label, e.target.checked)}
        />
      </Stack>
    </Box>
  );

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        boxShadow: 3,
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <IconButton>
            {icon}
          </IconButton>

          <Typography variant="h6" fontWeight={600}>
            {moduleName
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ flex: 1 }}>
          {(availableActions as PermissionKey[]).map(renderRow)}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mt: "auto" }}
        >
          <Button variant="outlined" disabled={isViewMode} onClick={handleClearAll}>
            Clear
          </Button>
          <Button variant="contained" disabled={isViewMode} onClick={handleSelectAll}>
            Select All
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default PermissionCard;